import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AuditTimelineResponse,
  CreateOpportunityInput,
  ListResponse,
  Opportunity,
  ReassignOwnerInput,
  Stage,
  UpdateOpportunityInput,
  YearsResponse,
} from "@salesboard/contracts";
import { useT, type Translator } from "@/i18n";
import { ApiClientError, api, toQueryString } from "@/lib/api";
import { reportMutationError, reportMutationSuccess } from "@/lib/mutations";

const KEY = "opportunities";

export function useOpportunities(
  // Arrays are the multi-value filters — `toQueryString` joins them with commas, which is
  // the comma-split vocabulary the endpoint's schema expects.
  params: Record<string, string | number | (string | number)[] | undefined>,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [KEY, params],
    queryFn: () => api.get<ListResponse<Opportunity>>(`/opportunities${toQueryString(params)}`),
    // The pipeline list waits for the year window: an unscoped read would paint every
    // year's opportunities — with a total and a page count computed over the wrong set —
    // and then replace them a moment later.
    enabled: options.enabled ?? true,
  });
}

export function useOpportunity(id: string | undefined) {
  return useQuery({
    queryKey: [KEY, id],
    queryFn: () => api.get<Opportunity>(`/opportunities/${id}`),
    enabled: Boolean(id),
  });
}

/**
 * One opportunity by its URL slug, following a retired slug through history server-side.
 *
 * Keyed by slug rather than id because the slug is what the route has; the response carries
 * the record's canonical slug, which is how a stale link corrects its own address bar.
 */
export function useOpportunityBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: [KEY, "by-slug", slug],
    queryFn: () => api.get<Opportunity>(`/opportunities/by-slug/${encodeURIComponent(slug!)}`),
    enabled: Boolean(slug),
  });
}

export function useStages() {
  return useQuery({
    queryKey: ["stages"],
    queryFn: () => api.get<Stage[]>("/opportunities/stages"),
    // Reference data from a migration; it does not change while someone is looking.
    staleTime: 60 * 60 * 1000,
  });
}

export function useOwners() {
  return useQuery({
    queryKey: ["owners"],
    queryFn: () => api.get<{ id: string; name: string }[]>("/opportunities/owners"),
  });
}

/**
 * The years the pipeline can be scoped to, and which one is current.
 *
 * From the server rather than `new Date().getFullYear()`: on 31 December a browser outside
 * `APP_TIMEZONE` disagrees about the year, and the board and the list would then default to
 * different data. Cached long — the window turns over once a year.
 */
export function useYears() {
  return useQuery({
    queryKey: ["opportunity-years"],
    queryFn: () => api.get<YearsResponse>("/opportunities/years"),
    staleTime: 60 * 60 * 1000,
  });
}

export function useOpportunityHistory(id: string | undefined, limit?: number) {
  return useQuery({
    queryKey: [KEY, id, "history", limit ?? "default"],
    queryFn: () =>
      api.get<AuditTimelineResponse & { total: number }>(`/opportunities/${id}/history${toQueryString({ limit })}`),
    enabled: Boolean(id),
  });
}

export function useCreateOpportunity() {
  const queryClient = useQueryClient();
  const t = useT();
  return useMutation({
    mutationFn: (input: CreateOpportunityInput) => api.post<Opportunity>("/opportunities", input),
    onSuccess: (opportunity) => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
      void queryClient.invalidateQueries({ queryKey: ["clients"] });
      reportMutationSuccess(t("toast.created", opportunity.name));
    },
    onError: (error) => reportMutationError(error),
  });
}

/**
 * One field, one request.
 *
 * The detail saves per field now, so this is called with exactly what changed plus the
 * version the value was read at. `mutateAsync` **awaits the refetch** so the inline field
 * returns to display mode on the server's committed value rather than on the one that was
 * typed.
 *
 * A refused stale write invalidates too. There is no reload affordance in this product by
 * design, so the refusal itself has to restore freshness — otherwise the field would keep
 * being refused with the old value still on screen and no way forward.
 */
export function useUpdateOpportunity(id: string) {
  const queryClient = useQueryClient();
  const t = useT();
  return useMutation({
    mutationFn: (input: UpdateOpportunityInput) => api.patch<Opportunity>(`/opportunities/${id}`, input),
    onSuccess: async (_opportunity, input) => {
      await queryClient.invalidateQueries({ queryKey: [KEY] });
      void queryClient.invalidateQueries({ queryKey: ["clients"] });
      reportMutationSuccess(savedMessage(t, input));
    },
    onError: (error) => {
      if (error instanceof ApiClientError && error.isStaleVersion) {
        void queryClient.invalidateQueries({ queryKey: [KEY] });
      }
      reportMutationError(error);
    },
  });
}

/**
 * What a per-field save says when it lands.
 *
 * Named per field rather than a generic "Opportunity saved": with one request per value, a
 * repeated identical toast tells the reader nothing about which of their three edits
 * committed.
 */
function savedMessage(t: Translator, input: UpdateOpportunityInput): string {
  if (input.notes !== undefined) return input.notes === null ? t("toast.removedNote") : t("toast.savedNote");
  if (input.name !== undefined) return t("toast.savedName");
  if (input.nip !== undefined) return t("toast.savedRevenue");
  if (input.expectedCloseDate !== undefined) return t("toast.savedCloseDate");
  if (input.clientId !== undefined) return t("toast.savedClient");
  return t("toast.savedOpportunity");
}

/**
 * Reassignment. Head of Sales only, gated by the API as well as by the interface, and it
 * **does not reset the stage timer** — the server is where that is guaranteed.
 */
export function useReassignOwner(id: string) {
  const queryClient = useQueryClient();
  const t = useT();
  return useMutation({
    mutationFn: (input: ReassignOwnerInput) => api.post<Opportunity>(`/opportunities/${id}/owner`, input),
    onSuccess: async (opportunity) => {
      // Both, because the owner's tint is on every card of theirs and the board reads it.
      await queryClient.invalidateQueries({ queryKey: [KEY] });
      reportMutationSuccess(t("toast.reassignedTo", opportunity.ownerName));
    },
    onError: (error) => {
      if (error instanceof ApiClientError && error.isStaleVersion) {
        void queryClient.invalidateQueries({ queryKey: [KEY] });
      }
      reportMutationError(error);
    },
  });
}

export function useDeleteOpportunity() {
  const queryClient = useQueryClient();
  const t = useT();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      api.delete<void>(`/opportunities/${id}${toQueryString({ version })}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
      void queryClient.invalidateQueries({ queryKey: ["clients"] });
      reportMutationSuccess(t("toast.opportunityDeleted"));
    },
    onError: (error) => reportMutationError(error),
  });
}
